import React, { useEffect, useState } from "react";
import { Ticket, Plus, Trash2, Pencil, Check, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const blank = { title: "", description: "", price: 2.99, perks: [], active: true };

export default function GamePassBuilder({ gameId }) {
  const [perks, setPerks] = useState({});
  const [passes, setPasses] = useState([]);
  const [editing, setEditing] = useState(null); // pass id, "new", or null
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/games/${gameId}/passes`).then((r) => setPasses(r.data.passes)).catch(() => {});
  useEffect(() => {
    api.get("/passes/perks").then((r) => setPerks(r.data.perks)).catch(() => {});
    load(); // eslint-disable-next-line
  }, [gameId]);

  const startNew = () => { setForm(blank); setEditing("new"); };
  const startEdit = (p) => { setForm({ title: p.title, description: p.description, price: p.price, perks: p.perks, active: p.active }); setEditing(p.id); };
  const cancel = () => { setEditing(null); setForm(blank); };
  const togglePerk = (k) => setForm((f) => ({ ...f, perks: f.perks.includes(k) ? f.perks.filter((x) => x !== k) : [...f.perks, k] }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Give your pass a title"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post(`/games/${gameId}/passes`, form);
      else await api.put(`/passes/${editing}`, form);
      toast.success("Pass saved!");
      cancel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this game pass?")) return;
    await api.delete(`/passes/${id}`);
    toast.success("Pass deleted"); load();
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="game-pass-builder">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-bold flex items-center gap-2"><Ticket className="w-5 h-5 text-[hsl(var(--primary))]" /> Game Pass Builder</h3>
        {editing === null && <Button size="sm" variant="outline" className="rounded-full" onClick={startNew} data-testid="pass-new-btn"><Plus className="w-4 h-4" /> New pass</Button>}
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">Bundle perks into a buyable pass (Stripe TEST mode). Kids' purchases need parent approval.</p>

      {editing !== null && (
        <div className="rounded-xl border border-[hsl(var(--primary))]/30 p-4 mb-4 space-y-3" data-testid="pass-editor">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><Label>Pass title</Label><Input value={form.title} maxLength={60} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="VIP Season Pass" className="mt-1" data-testid="pass-title" /></div>
            <div><Label>Price (USD)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="pass-price" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} maxLength={300} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What players get with this pass…" className="mt-1" data-testid="pass-desc" /></div>
          <div>
            <Label className="mb-2 block">Perks included</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(perks).map(([k, v]) => (
                <button key={k} onClick={() => togglePerk(k)} data-testid={`pass-perk-${k}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-all flex items-center gap-1.5 ${form.perks.includes(k) ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
                  <span>{v.icon}</span> {v.label} {form.perks.includes(k) && <Check className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => setForm((f) => ({ ...f, active: !f.active }))} data-testid="pass-active-toggle"
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${form.active ? "bg-emerald-500/15 text-emerald-500" : "bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))]"}`}>
              {form.active ? "● Active (visible to players)" : "○ Hidden"}
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={cancel} data-testid="pass-cancel"><X className="w-4 h-4" /> Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} className="rounded-full" data-testid="pass-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save pass
              </Button>
            </div>
          </div>
        </div>
      )}

      {passes.length === 0 && editing === null ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">No passes yet. Create a VIP pass, season pass or supporter pass to give fans extra perks.</p>
      ) : (
        <div className="space-y-2" data-testid="pass-list">
          {passes.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`pass-row-${p.id}`}>
              <Ticket className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">{p.title}
                  {!p.active && <Badge variant="outline" className="text-[9px]">hidden</Badge>}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">${p.price.toFixed(2)} · {p.perks.length} perk{p.perks.length === 1 ? "" : "s"}</div>
              </div>
              <button onClick={() => startEdit(p)} data-testid={`pass-edit-${p.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(p.id)} data-testid={`pass-del-${p.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
